import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Receipt, Heart, Settings, ArrowRight } from "lucide-react";
import { RecentPurchases } from "@/components/account/recent-purchases";

export const Route = createFileRoute("/_authenticated/_account/dashboard")({
  head: () => ({
    meta: [
      { title: "Account overview — CoderLity" },
      { name: "description", content: "Your CoderLity account overview." },
    ],
  }),
  component: DashboardPage,
});

type Profile = { display_name: string | null };

function DashboardPage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("display_name")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => setProfile(data ?? null));
  }, [user]);

  const stats = [
    { label: "Active projects", value: "—", icon: LayoutDashboard },
    { label: "Open invoices", value: "—", icon: Receipt },
    { label: "Saved items", value: "—", icon: Heart },
    { label: "Settings", value: "View", icon: Settings },
  ];

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold">
          Welcome back{profile?.display_name ? `, ${profile.display_name}` : ""}
        </h1>
        <p className="text-sm text-muted-foreground">Here's a snapshot of your account.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <Card key={s.label}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{s.label}</CardTitle>
              <s.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{s.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <RecentPurchases />

      <div className="grid gap-4 md:grid-cols-2">

        <Card>
          <CardHeader>
            <CardTitle>Complete your profile</CardTitle>
            <CardDescription>Add a display name, photo, and bio.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild size="sm" variant="outline" className="gap-2">
              <Link to="/profile">Edit profile <ArrowRight className="h-4 w-4" /></Link>
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Secure your account</CardTitle>
            <CardDescription>Change your password and review sessions.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild size="sm" variant="outline" className="gap-2">
              <Link to="/security">Security settings <ArrowRight className="h-4 w-4" /></Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
